import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

const IndustryPage: React.FC = () => {
  const { industryId } = useParams<{ industryId: string }>();
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [heroImageLoaded, setHeroImageLoaded] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const industries = [
    {
      id: "banking",
      name: "Banking",
      punchyLine: "Trust every account onboarded",
      description: "Our Smart Intelligence spots mule accounts, synthetic IDs, and laundering networks, delivering real-time AI insights to flag hidden patterns and block high-risk users.",
      icon: (
        <svg className="w-6 h-6 text-[#26ab4d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      industryRequirements: [
        "Fraud Control: Detect mule accounts and synthetic identities in real time across digital, mobile, and branch channels before they enter your ecosystem",
        "Customer Truth: Use behavioural and data intelligence to separate genuine customers from fraudsters with accuracy not with guesswork or assumptions",
        "Smart Compliance: Meet AML, KYC, and sanction norms without slowing onboarding flows or disrupting user experience across banking journeys",
        "Risk Scalability: Manage high alert volumes with automated triage, smart prioritization, and faster investigations that keep risk teams efficient and proactive"
      ],
      fraudLandscape: "Fraudsters are evolving fast - using mule accounts, synthetic IDs, and laundering networks to exploit system gaps. Bargad stops them before they strike, protecting trust and compliance.",
      howBargadHelps: [
        "Smart Behavioural Tracking: Bargad monitors every user journey and transaction flow to detect micro-deviations that signal mule or fraud activity. Our adaptive AI learns in real time-enhancing detection accuracy without disrupting genuine users.",
        "Relationship Mapping: We connect users, devices, accounts, and transactions to reveal hidden links missed by traditional systems. This network view helps banks identify suspicious clusters and shut down fraud rings early.",
        "Automated Fraud Response: Bargad's risk engine instantly flags anomalies, prioritizes alerts, and triggers response workflows-enabling immediate action, faster decisions, and reduced losses.",
        "Plug-and-Play Integration: With an API-first architecture, Bargad integrates seamlessly into existing banking and compliance systems. Fast to deploy and easy to scale, it strengthens your fraud defense without adding complexity."
      ],
      keyCapabilities: [
        "Smarter Fraud Detection: Uncover mule accounts, fake identities, and hidden fraud rings in real time. Bargad's adaptive AI learns every interaction - ensuring sharper accuracy and fewer false positives.",
        "Faster, Smoother Operations: Automated workflows eliminate manual checks and slow reviews. Fraud decisions happen instantly - keeping onboarding and transactions effortless.",
        "Built-In Compliance: Stay ahead of AML, KYC, and FATF requirements with auto-verified data trails and real-time audit readiness. Compliance, simplified.",
        "Lower Risk. Higher Trust: Detect early, act faster, and protect smarter. Bargad strengthens your defense - cutting financial risk while securing customer confidence."
      ]
    },
    {
      id: "nbfc",
      name: "Lending",
      punchyLine: "Lend safely with smart signals",
      description: "Our Smart Intelligence detects synthetic identities and fake borrower profiles before loans are disbursed, uncovering linked accounts and flagging high-risk applications to prevent fraud.",
      icon: (
        <svg className="w-6 h-6 text-[#26ab4d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      industryRequirements: [
        "Borrower Screening: Fraudsters use fake or stolen identities to secure loans. Bargad detects synthetic profiles and mule linkages early to prevent losses at approval",
        "Intent Verification: Loan fraud hides behind valid documents. Bargad checks behaviour, devices, and journey patterns to confirm genuine borrowers and block impersonators",
        "Clean Compliance: Stay compliant with automated AML and KYC checks without slowing loan processing or disrupting onboarding flows",
        "Scalable Checks: High loan volumes cause alert fatigue. Bargad's smart triage highlights risky cases instantly to reduce losses and protect reputation"
      ],
      fraudLandscape: "With rising synthetic identities and mule networks, NBFCs face hidden threats that erode financial integrity.",
      howBargadHelps: "Bargad.ai fuses behavioural analytics and link intelligence to uncover invisible risk patterns, empowering lenders to make faster, safer credit decisions.",
      keyCapabilities: [
        "Real-time mule and network detection",
        "Adaptive AI-driven risk scoring",
        "Instant compliance with minimal friction",
        "Seamless API integration"
      ]
    },
    {
      id: "insurance",
      name: "Insurance",
      punchyLine: "Stop fraud before claims start",
      description: "Our Smart Intelligence detects fake customers, forged agents, and synthetic documents before policy issuance, uncovering hidden links and flagging high-risk profiles to prevent fraudulent claims.",
      icon: (
        <svg className="w-6 h-6 text-[#26ab4d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      industryRequirements: [
        "Profile Screening: Fraudsters use forged documents and synthetic identities to buy policies and file false claims. Early detection prevents major financial loss",
        "Agent Validation: Some agents assist fraud networks by creating fake profiles or inflated claims. Bargad validates agent behaviour and hidden relationships",
        "Document Integrity: Forged IDs, medical records, or nominee details help fraudsters inflate claims. Real-time document intelligence blocks these attempts instantly",
        "Safe Onboarding: Fraud starts at onboarding. Detect intent and hidden linkages before policy issuance to ensure only genuine customers enter your system"
      ],
      fraudLandscape: "From ghost brokers to cloned policies, insurance fraud starts at onboarding and matures into costly claims.",
      howBargadHelps: "Bargad.ai analyses behavioural, device, and network signals to reveal intent early - ensuring genuine customers pass through while fraudsters are filtered out.",
      keyCapabilities: [
        "Detect fake or stolen identities instantly",
        "Identify collusive or repeated fraud rings",
        "Pre-onboarding risk scoring",
        "Lower claim losses and stronger trust"
      ]
    },
    {
      id: "gaming",
      name: "Gaming",
      punchyLine: "Ensure fair play from onboarding",
      description: "Our Smart Intelligence detects fake players, underage profiles, and manipulated controls before tournaments start, flagging high-risk users and exposing mule links to ensure fair gaming.",
      icon: (
        <svg className="w-6 h-6 text-[#26ab4d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      industryRequirements: [
        "Player Screening: Fraudsters use synthetic or underage profiles to bypass controls. Early screening ensures only genuine players join tournaments",
        "Fair Gameplay: Bad actors exploit systems to rig outcomes. Real-time behaviour checks detect unfair play and block manipulation instantly",
        "Mule Prevention: Fraudsters route winnings to mule accounts. Network and transaction analysis prevents such fraudulent money movements",
        "Secure Onboarding: Fraud begins at signup. Smart AI filters risky players at acquisition to maintain fair play and ecosystem safety"
      ],
      fraudLandscape: "Gaming platforms face constant abuse - bots, fake profiles, bonus manipulation, and mule networks that distort fair competition and revenue.",
      howBargadHelps: "Bargad.ai blends behavioural and device intelligence to monitor gameplay, purchases, and interactions in real time - keeping fraud checks invisible while preserving the player experience.",
      keyCapabilities: [
        "Detect fake players, bots, and mule networks before entry",
        "Protect in-game currencies and promo programs",
        "Continuous anomaly monitoring across gameplay and payments",
        "Maintain trust through fair, transparent play"
      ]
    },
    {
      id: "matrimony",
      name: "Matrimony",
      punchyLine: "Stop fake profiles before matches",
      description: "Our Smart Intelligence uncovers linked identities, flags high-risk users, and detects fake profiles, forged documents, and hidden motives to prevent fraud before matches are made.",
      icon: (
        <svg className="w-6 h-6 text-[#26ab4d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
      industryRequirements: [
        "Profile Accuracy: Fraudsters use forged income or property papers to mislead users. Early verification prevents deceit before matches happen",
        "Intent Screening: Some join platforms for financial gain or misuse after marriage. Tools are needed to detect suspicious patterns from the start",
        "Document Checks: Fake salary slips or home documents mislead genuine users. Smart document checks ensure authenticity before commitment",
        "Platform Trust: Prevent emotional and financial fraud by validating users and claims that ensuring every match remains real and trustworthy"
      ],
      fraudLandscape: "Romance scams, catfishing, and synthetic identities damage emotions and reputations long before discovery.",
      howBargadHelps: "Bargad.ai merges identity, device, social, and behavioural intelligence to verify intent, unmask deception, and secure genuine connections - all while protecting privacy.",
      keyCapabilities: [
        "Unmask fake profiles via image, metadata, and reuse analysis",
        "Verify intent through behavioural and messaging patterns",
        "Expose hidden networks linking scam rings and ghost brokers",
        "Prioritise risky accounts for faster moderation",
        "Enforce safety without exposing user data"
      ]
    },
    {
      id: "ecommerce",
      name: "E-Commerce",
      punchyLine: "Spot risky buyers before checkout",
      description: "Our Smart Intelligence detects fake customers, risky deliveries, and refund scams before transactions, flagging suspicious behaviour and uncovering linked accounts to ensure secure e-commerce.",
      icon: (
        <svg className="w-6 h-6 text-[#26ab4d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
      ),
      industryRequirements: [
        "Order Screening: Identify fake or risky orders using behaviour and device intelligence, stopping refund scams and cancellations early",
        "Rider Validation: Validate delivery-partner identity, behaviour, and background to prevent misuse, collusion, or unsafe deliveries",
        "Refund Control: Spot abnormal refund patterns, fake complaints, and repeated misuse that cause revenue loss and poor customer experience",
        "Platform Safety: Detect illegal shipments, suspicious users, and hidden linkages across customers and partners with protecting trust and reputation"
      ],
      fraudLandscape: "E-commerce platforms lose margin to bots, promo abuse, fake listings, and refund manipulation - threatening both profit and trust.",
      howBargadHelps: "Bargad.ai unifies behavioural, image, and marketplace intelligence to secure checkout, vet sellers, and ensure authentic interactions without friction.",
      keyCapabilities: [
        "Block scalpers, automated checkout, and inventory hoarding",
        "Detect coupon stacking and coordinated offer exploitation",
        "Validate merchant identities and flag counterfeit listings",
        "Catch refund/return scams through device-location correlation",
        "Preserve review integrity via cross-account linkage and image matching"
      ]
    }
  ];

  // Function to get industry-specific vector
  const getIndustryVector = (industryId: string) => {
    const gradientId = `gradient-${industryId}`;
    
    switch (industryId) {
      case 'banking':
        return (
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#26ab4d" />
                <stop offset="100%" stopColor="#1e8a3f" />
              </linearGradient>
            </defs>
            <rect x="10" y="20" width="60" height="40" rx="4" fill="url(#gradientId)" opacity="0.8"/>
            <rect x="15" y="25" width="50" height="30" rx="2" fill="url(#gradientId)"/>
            <rect x="20" y="30" width="8" height="20" fill="url(#gradientId)"/>
            <rect x="32" y="30" width="8" height="20" fill="url(#gradientId)"/>
            <rect x="44" y="30" width="8" height="20" fill="url(#gradientId)"/>
            <rect x="56" y="30" width="8" height="20" fill="url(#gradientId)"/>
            <circle cx="40" cy="15" r="3" fill="url(#gradientId)"/>
            <path d="M35 15 L40 10 L45 15" stroke="url(#gradientId)" strokeWidth="2" fill="none"/>
          </svg>
        );
      case 'nbfc':
        return (
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#26ab4d" />
                <stop offset="100%" stopColor="#1e8a3f" />
              </linearGradient>
            </defs>
            <circle cx="40" cy="40" r="30" fill="url(#gradientId)" opacity="0.8"/>
            <path d="M25 40 L35 30 L45 30 L55 40 L45 50 L35 50 Z" fill="url(#gradientId)"/>
            <circle cx="40" cy="25" r="2" fill="url(#gradientId)"/>
            <circle cx="40" cy="55" r="2" fill="url(#gradientId)"/>
          </svg>
        );
      case 'insurance':
        return (
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#26ab4d" />
                <stop offset="100%" stopColor="#1e8a3f" />
              </linearGradient>
            </defs>
            <path d="M40 10 L50 20 L50 50 L40 60 L30 50 L30 20 Z" fill="url(#gradientId)" opacity="0.8"/>
            <path d="M40 15 L45 20 L45 45 L40 50 L35 45 L35 20 Z" fill="url(#gradientId)"/>
            <circle cx="40" cy="35" r="8" fill="url(#gradientId)"/>
            <rect x="35" y="55" width="10" height="15" rx="2" fill="url(#gradientId)"/>
          </svg>
        );
      case 'gaming':
        return (
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#26ab4d" />
                <stop offset="100%" stopColor="#1e8a3f" />
              </linearGradient>
            </defs>
            <rect x="20" y="25" width="40" height="25" rx="8" fill="url(#gradientId)" opacity="0.8"/>
            <circle cx="30" cy="35" r="3" fill="url(#gradientId)"/>
            <circle cx="50" cy="35" r="3" fill="url(#gradientId)"/>
            <rect x="35" y="40" width="10" height="5" rx="2" fill="url(#gradientId)"/>
            <rect x="15" y="30" width="8" height="3" rx="1" fill="url(#gradientId)"/>
            <rect x="57" y="30" width="8" height="3" rx="1" fill="url(#gradientId)"/>
            <rect x="15" y="40" width="8" height="3" rx="1" fill="url(#gradientId)"/>
            <rect x="57" y="40" width="8" height="3" rx="1" fill="url(#gradientId)"/>
            <circle cx="40" cy="15" r="2" fill="url(#gradientId)"/>
            <path d="M38 15 L40 13 L42 15" stroke="url(#gradientId)" strokeWidth="1" fill="none"/>
          </svg>
        );
      case 'matrimony':
        return (
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#26ab4d" />
                <stop offset="100%" stopColor="#1e8a3f" />
              </linearGradient>
            </defs>
            <circle cx="30" cy="35" r="12" fill="url(#gradientId)" opacity="0.8"/>
            <circle cx="50" cy="35" r="12" fill="url(#gradientId)" opacity="0.8"/>
            <path d="M30 47 Q40 55 50 47" stroke="url(#gradientId)" strokeWidth="3" fill="none"/>
            <circle cx="30" cy="30" r="3" fill="url(#gradientId)"/>
            <circle cx="50" cy="30" r="3" fill="url(#gradientId)"/>
            <path d="M25 30 Q30 25 35 30" stroke="url(#gradientId)" strokeWidth="2" fill="none"/>
            <path d="M45 30 Q50 25 55 30" stroke="url(#gradientId)" strokeWidth="2" fill="none"/>
            <circle cx="40" cy="15" r="2" fill="url(#gradientId)"/>
            <path d="M38 15 L40 13 L42 15" stroke="url(#gradientId)" strokeWidth="1" fill="none"/>
          </svg>
        );
      case 'ecommerce':
        return (
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#26ab4d" />
                <stop offset="100%" stopColor="#1e8a3f" />
              </linearGradient>
            </defs>
            <rect x="20" y="25" width="40" height="30" rx="4" fill="url(#gradientId)" opacity="0.8"/>
            <rect x="25" y="30" width="30" height="20" rx="2" fill="url(#gradientId)"/>
            <circle cx="30" cy="35" r="2" fill="url(#gradientId)"/>
            <rect x="35" y="33" width="15" height="4" rx="2" fill="url(#gradientId)"/>
            <rect x="25" y="40" width="30" height="2" rx="1" fill="url(#gradientId)"/>
            <rect x="25" y="45" width="20" height="2" rx="1" fill="url(#gradientId)"/>
            <path d="M15 20 L20 15 L25 20" stroke="url(#gradientId)" strokeWidth="2" fill="none"/>
            <path d="M55 20 L60 15 L65 20" stroke="url(#gradientId)" strokeWidth="2" fill="none"/>
            <circle cx="40" cy="15" r="2" fill="url(#gradientId)"/>
            <path d="M38 15 L40 13 L42 15" stroke="url(#gradientId)" strokeWidth="1" fill="none"/>
          </svg>
        );
      default:
        return (
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#26ab4d" />
                <stop offset="100%" stopColor="#1e8a3f" />
              </linearGradient>
            </defs>
            <circle cx="40" cy="40" r="25" fill="url(#gradientId)" opacity="0.8"/>
            <path d="M30 40 L35 35 L45 35 L50 40 L45 45 L35 45 Z" fill="url(#gradientId)"/>
            <circle cx="40" cy="25" r="2" fill="url(#gradientId)"/>
            <circle cx="40" cy="55" r="2" fill="url(#gradientId)"/>
          </svg>
        );
    }
  };

  const industry = industries.find(ind => ind.id === industryId);

  // Reset image loaded state when industry changes
  useEffect(() => {
    setHeroImageLoaded(true);
    // Fade out after a short delay
    const timer = setTimeout(() => {
      setHeroImageLoaded(false);
    }, 100);
    return () => clearTimeout(timer);
  }, [industryId]);

  // Ensure video plays when component mounts or industry changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load(); // Reload video to ensure it starts fresh
      videoRef.current.play().catch(() => {
        // Ignore autoplay errors (browser may block autoplay)
      });
    }
  }, [industryId]);

  // Lightweight CountUp component for numbers
  const CountUp: React.FC<{ target: number; durationMs?: number; prefix?: string; suffix?: string }> = ({
    target,
    durationMs = 1500,
    prefix = '',
    suffix = ''
  }) => {
    const [value, setValue] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const startTsRef = useRef<number | null>(null);
    const elementRef = useRef<HTMLSpanElement>(null);
    const animationIdRef = useRef<number | null>(null);

    const formatted = useMemo(() => {
      const formattedNumber = Math.floor(value).toLocaleString('en-IN');
      return `${prefix}${formattedNumber}${suffix}`;
    }, [value, prefix, suffix]);

    // Observe when element enters viewport
    useEffect(() => {
      const element = elementRef.current;
      if (!element) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible(true);
              // Reset animation when entering viewport
              setValue(0);
              startTsRef.current = null;
            } else {
              setIsVisible(false);
            }
          });
        },
        { threshold: 0.1 }
      );

      observer.observe(element);
      return () => observer.disconnect();
    }, []);

    // Animate when visible
    useEffect(() => {
      if (!isVisible) return;

      let rafId: number;
      const animate = (ts: number) => {
        if (startTsRef.current === null) startTsRef.current = ts;
        const elapsed = ts - startTsRef.current;
        const progress = Math.min(1, elapsed / durationMs);
        const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
        setValue(eased * target);
        if (progress < 1) {
          rafId = requestAnimationFrame(animate);
          animationIdRef.current = rafId;
        } else {
          animationIdRef.current = null;
        }
      };
      rafId = requestAnimationFrame(animate);
      animationIdRef.current = rafId;
      
      return () => {
        if (animationIdRef.current) {
          cancelAnimationFrame(animationIdRef.current);
          animationIdRef.current = null;
        }
      };
    }, [target, durationMs, isVisible]);

    return <span ref={elementRef}>{formatted}</span>;
  };

  // Industry-specific content helpers
  const isLending = industryId === 'nbfc';
  const isInsurance = industryId === 'insurance';
  const isGaming = industryId === 'gaming';
  const isEcommerce = industryId === 'ecommerce';
  const isMatrimony = industryId === 'matrimony';
  const fraudPunchlineText = isLending
    ? 'Fast lending needs faster fraud defence.'
    : isInsurance
    ? 'Smart insurance starts with smarter fraud defence.'
    : isGaming
    ? 'Fair Play Needs Smarter Fraud Defence.'
    : isEcommerce
    ? 'Smart commerce needs smarter fraud defence.'
    : isMatrimony
    ? 'Spot fake intent before vows are made.'
    : 'Fraud evolves fast, your defence should move faster.';
  const fraudDescriptionText = isLending
    ? 'Fraudsters misuse synthetic identities, stacked loans, and fake devices to slip through digital onboarding. These blind spots cause major credit and reputation risks. Bargad.ai detects hidden connections, unusual behaviour, and device-level threats in real time and stopping fraud before funds are disbursed.'
    : isInsurance
    ? 'Fraudsters and fake agents use synthetic documents to get high-value policies and later file fake claims through nominee accounts. Bargad.ai spots hidden links, fake identities, and suspicious intent early and stopping fraud before the policy begins.'
    : isGaming
    ? 'Fraudsters use fake IDs, underage profiles, and manipulated controls to rig tournaments and divert winnings to mule accounts. Bargad.ai detects suspicious intent, fake players, and linked accounts early and ensuring fair play and genuine competition from signup to payout.'
    : isEcommerce
    ? 'E-commerce fraud now hits both customers and delivery partners from fake cancellations and refund scams to risky or illegal deliveries. Bargad.ai detects hidden intent, linked accounts, and suspicious behaviour early, ensuring every order, rider, and transaction stays safe and genuine.'
    : isMatrimony
    ? 'Fraudsters use fake profiles, forged documents, and hidden motives to deceive genuine users and leading to emotional and financial loss after marriage. Bargad.ai detects deceptive intent, verifies identities, and uncovers hidden links early and keeping your platform real, safe, and trustworthy.'
    : 'Banks face rising threats from mule accounts, fake identities, and laundering networks that slip past traditional tools. Bargad.ai uses real-time AI intelligence to detect hidden links and suspicious behaviour right at onboarding, protecting revenue, trust, and compliance from day one.';

  // How Bargad Helps interactive content
  const helpsItems = isLending
    ? [
        {
          id: 'detect-synthetic',
          title: 'Detect Synthetic Borrowers Early',
          description:
            'Bargad flags fake or duplicate identities before loan approval stopping fraudsters from using synthetic docs or recycled accounts to secure credit.',
          image: '/web vectors/web vectors/lending.png',
        },
        {
          id: 'behavioural-scoring',
          title: 'Real-Time Behavioural Scoring',
          description:
            'Our AI tracks borrower activity and device signals in real time, updating risk scores dynamically to prevent loans from going to risky or linked profiles.',
          image: '/web vectors/web vectors/device forensics.png',
        },
        {
          id: 'expose-linkages',
          title: 'Expose Hidden Linkages',
          description:
            'Bargad maps connections between users, devices, and accounts to reveal collusive groups and repeat fraud rings operating across platforms.',
          image: '/web vectors/web vectors/network connectivity.png',
        },
        {
          id: 'faster-decisions',
          title: 'Faster, Smarter Decisions',
          description:
            'With automated risk triage and prioritized alerts, NBFC teams can focus on genuine borrowers, approve faster, and block fraud before disbursement.',
          image: '/web vectors/web vectors/acquisition funnel.png',
        },
      ]
    : isInsurance
    ? [
        {
          id: 'fake-customers',
          title: 'Detect Fake Customers & Agents',
          description:
            'Bargad identifies fraudulent customers and collusive agents using behavioural analytics, device intelligence, and network mapping and stopping fraud at the source.',
          image: '/web vectors/web vectors/banking.png',
        },
        {
          id: 'verify-docs',
          title: 'Verify Documents in Real Time',
          description:
            'Our AI detects forged or tampered documents, mismatched details, and reused identities before policy approval, preventing fake onboarding.',
          image: '/web vectors/web vectors/forged doc.png',
        },
        {
          id: 'hidden-connections',
          title: 'Expose Hidden Connections',
          description:
            'Bargad links customers, agents, and nominee accounts to uncover fraud rings and repeated claim manipulation across policies.',
          image: '/web vectors/web vectors/network connectivity.png',
        },
        {
          id: 'act-before-claim',
          title: 'Act Before the Claim',
          description:
            'Real-time risk scoring alerts insurers to suspicious intent early, helping block high-risk policies before losses occur.',
          image: '/web vectors/web vectors/transaction patterns.png',
        },
      ]
    : isGaming
    ? [
        {
          id: 'verify-players',
          title: 'Verify Players at Onboarding',
          description:
            'Bargad screens players during registration that validating age, identity, and intent to stop fake or underage profiles before entry.',
          image: '/web vectors/web vectors/kyc.png',
        },
        {
          id: 'detect-manipulation',
          title: 'Detect Game Manipulation',
          description:
            'Our AI monitors player actions, device signals, and behavioural patterns to catch rigging or control tampering in real time.',
          image: '/web vectors/web vectors/device forensics.png',
        },
        {
          id: 'block-mule-payouts',
          title: 'Block Mule Payouts',
          description:
            'Bargad traces links between players, wallets, and nominee accounts to stop fraudulent winnings from reaching mule networks.',
          image: '/web vectors/web vectors/network connectivity.png',
        },
        {
          id: 'protect-fair-play',
          title: 'Protect Fair Play at Scale',
          description:
            'Seamless API integration enables continuous fraud checks without slowing tournaments and ensuring secure, transparent, and fair gameplay for all.',
          image: '/web vectors/web vectors/acquisition funnel.png',
        },
      ]
    : isEcommerce
    ? [
        {
          id: 'risky-orders',
          title: 'Detect Risky Orders in Real Time',
          description:
            'Bargad flags fake orders, refund abuse, and repeated cancellations using behavioural and transaction intelligence before losses occur.',
          image: '/web vectors/web vectors/transaction patterns.png',
        },
        {
          id: 'verify-riders',
          title: 'Verify Delivery Partner Authenticity',
          description:
            'AI checks rider identity, device signals, and route patterns to spot fake, collusive, or high-risk profiles instantly.',
          image: '/web vectors/web vectors/device forensics.png',
        },
        {
          id: 'suspicious-deliveries',
          title: 'Expose Suspicious Deliveries',
          description:
            'Bargad detects unusual parcel data, delivery routes, or sender-receiver links protecting riders from illegal or unsafe deliveries.',
          image: '/web vectors/web vectors/ecommerce fraud.png',
        },
        {
          id: 'smart-decisions',
          title: 'Enable Smart, Fast Decisions',
          description:
            'Our adaptive risk engine scores every order and user in milliseconds letting platforms block fraud and approve genuine activity without delay.',
          image: '/web vectors/web vectors/acquisition funnel.png',
        },
      ]
    : isMatrimony
    ? [
        {
          id: 'detect-fake-profiles',
          title: 'Detect Fake Profiles Early',
          description:
            'Bargad identifies forged profiles, fake documents, and hidden motives before matches are made keeping users safe from deceit.',
          image: '/web vectors/web vectors/image detetction.png',
        },
        {
          id: 'verify-intent',
          title: 'Verify Real Intent',
          description:
            'Behavioural analytics and social insights reveal users marrying for money or with fraudulent intent before trust is broken.',
          image: '/web vectors/web vectors/cognitive behaviour.png',
        },
        {
          id: 'validate-docs',
          title: 'Validate Shared Documents',
          description:
            'AI checks income, property, and business proofs for authenticity stopping fake claims before relationships move forward.',
          image: '/web vectors/web vectors/forged doc.png',
        },
        {
          id: 'expose-links',
          title: 'Expose Hidden Connections',
          description:
            'Bargad maps links between users and external fraud networks ensuring only genuine profiles and real relationships connect.',
          image: '/web vectors/web vectors/network connectivity.png',
        },
      ]
    : [
        {
          id: 'behavioural',
          title: 'Behavioural Tracking',
          description:
            "Bargad’s AI monitors every user journey to spot subtle behaviour shifts that signal mule or fraud activity and stop risk before it enters your system.",
          image: '/web vectors/web vectors/cognitive behaviour.png',
        },
        {
          id: 'relationship',
          title: 'Relationship Mapping',
          description:
            'We link users, devices, and transactions to uncover hidden connections and expose fraud rings invisible to traditional tools.',
          image: '/web vectors/web vectors/network connectivity.png',
        },
        {
          id: 'response',
          title: 'Automated Fraud Response',
          description:
            'Our risk engine flags anomalies, ranks alerts, and triggers instant actions helping banks act fast and cut losses early.',
          image: '/web vectors/web vectors/device forensics.png',
        },
        {
          id: 'integration',
          title: 'Seamless Integration',
          description:
            'Built with an API-first design, Bargad fits smoothly into existing systems, boosting fraud defence without adding complexity or friction.',
          image: '/web vectors/web vectors/acquisition funnel.png',
        },
      ];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [activeHelpIndex, setActiveHelpIndex] = useState<number>(0);

  // Map industry IDs to their "How Bargad Helps" vectors
  const howBargadHelpsImageMap: Record<string, string> = {
    'banking': '/how_bargad_helps/banking_hbh.png',
    'nbfc': '/how_bargad_helps/lending_hbh.png',
    'insurance': '/how_bargad_helps/insurance_hbh.png',
    'gaming': '/how_bargad_helps/ggaming_hbh.png',
    'matrimony': '/how_bargad_helps/matrimony_hbh.png',
    'ecommerce': '/how_bargad_helps/ecommerce_hbh.png',
  };

  // Get the industry-specific "How Bargad Helps" image
  const howBargadHelpsImage = howBargadHelpsImageMap[industryId || ''] || '/design1.png';

  // Key Capabilities content per industry (banking default vs lending)
  const keyCapTitles = isLending
    ? [
        'Smarter, Safer Lending',
        'Compliance Made Effortless',
        'Early Fraud Detection',
        'Faster Fraud Resolution'
      ]
    : isInsurance
    ? [
        'Cleaner Leads, Better Sales',
        'Smarter Risk Decisions',
        'Lower Claim Losses',
        'Stronger Compliance & Trust'
      ]
    : isGaming
    ? [
        'Block Fraud Before It Spreads',
        'Ensure Smooth Player Experience',
        'Protect Virtual Economies',
        'Enable Faster Response & Resolution'
      ]
    : isEcommerce
    ? [
        'Prevent Financial Drain',
        'Strengthen Trust & Retention',
        'Scale Fearlessly',
        'Intelligence that Drives Action'
      ]
    : isMatrimony
    ? [
        'Genuine Connections, Safer Platform',
        'Frictionless Verification Experience',
        'Stronger Security & Compliance',
        'Automated Moderation at Scale'
      ]
    : [
        'Smarter Fraud Detection',
        'Faster, Smoother Operations',
        'Built-In Compliance',
        'Lower Risk. Higher Trust'
      ];

  const keyCapBenefits: string[][] = isLending
    ? [
        ['Detect synthetic identities early', 'Secure disbursals proactively', 'Keep borrowers genuine'],
        ['Automate AML & KYC', 'Reduce manual errors', 'Ensure smooth approvals'],
        ['Identify fake borrowers', 'Uncover linked profiles', 'Protect credit pipeline'],
        ['Real-time risk insights', 'Prioritize critical threats', 'Prevent credit losses']
      ]
    : isInsurance
    ? [
        ['Filter Fraudulent Applications.', 'Protect Acquisition Budgets.', 'Focus on Genuine customers.'],
        ['AI-Driven Risk Scoring.', 'Behaviour-Based Insights.', 'Faster Underwriting Decisions.'],
        ['Detect Fake Identities Early.', 'Spot fake or ghost profiles.', 'Reduce inflated claim costs.'],
        ['Strengthen regulatory compliance.', 'Safeguard customer information.', 'Build lasting insurer credibility']
      ]
    : isGaming
    ? [
        ['Detect fake player profiles.', 'Stop bots and mule rings.', 'Protect tournaments and revenue'],
        ['Reduce false positives.', 'Maintain seamless gameplay.', 'Boost retention and trust.'],
        ['Secure in-game currencies.', 'Prevent reward exploitation.', 'Preserve digital ecosystem integrity.'],
        ['Get instant fraud alerts.', 'Act quickly with insights.', 'Keep gameplay fair and secure.']
      ]
    : isEcommerce
    ? [
        ['Stop refund manipulation.', 'Block chargebacks instantly.', 'Detect account-level fraud'],
        ['Secure every transaction.', 'Build customer confidence.', 'Ensure frictionless experience'],
        ['Grow with confidence.', 'Adapt to new risks.', 'Protect global expansion.'],
        ['Convert risk to insight.', 'Predict fraud patterns.', 'Act faster, prevent losses.']
      ]
    : isMatrimony
    ? [
        ['Remove fake profiles.', 'Block scam attempts.', 'Build trusted community.'],
        ['Quick user checks.', 'Smooth onboarding flow.', 'Zero friction security.'],
        ['Protect user data.', 'Ensure policy compliance.', 'Maintain platform integrity.'],
        ['Detect high-risk users.', 'Prioritize suspicious profiles.', 'Simplify fraud reviews.']
      ]
    : [
        ['Detect mules instantly', 'Learn from every action', 'Reduce false positives'],
        ['Automate risk reviews', 'Enable instant decisions', 'Keep onboarding seamless'],
        ['Auto-verify user data', 'Ensure AML–KYC alignment', 'Stay audit ready'],
        ['Detect early threats', 'Protect customer confidence', 'Strengthen financial defences']
      ];

  // Preload industry hero image IMMEDIATELY on mount for faster loading
  // Also preload other use case images in background for faster navigation between pages
  useEffect(() => {
    const imageUrls: Record<string, string> = {
      'banking': '/industry-hero-icons/banking fraud.png',
      'nbfc': '/industry-hero-icons/lending fraud.png',
      'insurance': '/industry-hero-icons/insurance fraud.png',
      'gaming': '/industry-hero-icons/game fraud.png',
      'matrimony': '/industry-hero-icons/matrimony fraud.png',
      'ecommerce': '/industry-hero-icons/ecommerce fraud.png',
    };
    
    const howBargadHelpsUrls: Record<string, string> = {
      'banking': '/how_bargad_helps/banking_hbh.png',
      'nbfc': '/how_bargad_helps/lending_hbh.png',
      'insurance': '/how_bargad_helps/insurance_hbh.png',
      'gaming': '/how_bargad_helps/ggaming_hbh.png',
      'matrimony': '/how_bargad_helps/matrimony_hbh.png',
      'ecommerce': '/how_bargad_helps/ecommerce_hbh.png',
    };
    
    const imageUrl = industryId ? imageUrls[industryId] : null;
    const howBargadHelpsUrl = industryId ? howBargadHelpsUrls[industryId] : null;
    const links: HTMLLinkElement[] = [];
    
    // Preload current page hero image with highest priority
    if (imageUrl) {
      // Method 1: Link preload with high priority (browser hint) - CRITICAL for fast loading
      const preloadLink = document.createElement('link');
      preloadLink.rel = 'preload';
      preloadLink.as = 'image';
      preloadLink.href = imageUrl;
      preloadLink.setAttribute('fetchpriority', 'high');
      document.head.appendChild(preloadLink);
      links.push(preloadLink);
      
      // Method 2: Force immediate fetch using Image constructor (parallel download)
      // This starts downloading immediately, even before React renders
      const img = new Image();
      img.src = imageUrl;
      // Set onload to ensure image is cached
      img.onload = () => {
        // Image is now in browser cache
      };
    }
    
    // Preload current page how_bargad_helps image with lower priority (not critical for initial render)
    if (howBargadHelpsUrl) {
      const img = new Image();
      img.src = howBargadHelpsUrl;
      
      const prefetchLink = document.createElement('link');
      prefetchLink.rel = 'prefetch';
      prefetchLink.href = howBargadHelpsUrl;
      document.head.appendChild(prefetchLink);
      links.push(prefetchLink);
    }
    
    // Preload OTHER use case images in background for faster navigation between pages
    // This ensures when user navigates from one use case to another, images are already cached
    Object.entries(imageUrls).forEach(([id, url]) => {
      if (id !== industryId) {
        // Prefetch other industry images with lower priority
        const prefetchLink = document.createElement('link');
        prefetchLink.rel = 'prefetch';
        prefetchLink.as = 'image';
        prefetchLink.href = url;
        document.head.appendChild(prefetchLink);
        links.push(prefetchLink);
      }
    });
    
    Object.entries(howBargadHelpsUrls).forEach(([id, url]) => {
      if (id !== industryId) {
        // Prefetch other how_bargad_helps images with lower priority
        const prefetchLink = document.createElement('link');
        prefetchLink.rel = 'prefetch';
        prefetchLink.as = 'image';
        prefetchLink.href = url;
        document.head.appendChild(prefetchLink);
        links.push(prefetchLink);
      }
    });
    
    return () => {
      // Cleanup: Remove preload links to avoid memory leaks
      links.forEach(link => {
        if (link && link.parentNode) {
          try {
            document.head.removeChild(link);
          } catch (e) {
            // Link may have already been removed
          }
        }
      });
    };
  }, [industryId]);

  // Reset image loaded state when industry changes
  useEffect(() => {
    setHeroImageLoaded(true);
    
    // If industry image exists, pre-warm the image immediately
    const imageUrls: Record<string, string> = {
      'banking': '/industry-hero-icons/banking fraud.png',
      'nbfc': '/industry-hero-icons/lending fraud.png',
      'insurance': '/industry-hero-icons/insurance fraud.png',
      'gaming': '/industry-hero-icons/game fraud.png',
      'matrimony': '/industry-hero-icons/matrimony fraud.png',
      'ecommerce': '/industry-hero-icons/ecommerce fraud.png',
    };
    
    const imageUrl = industryId ? imageUrls[industryId] : null;
    if (imageUrl) {
      const img = new Image();
      img.onload = () => {
        setTimeout(() => {
          setHeroImageLoaded(false);
        }, 50);
      };
      img.onerror = () => {
        setHeroImageLoaded(false);
      };
      img.src = imageUrl;
    } else {
      const timer = setTimeout(() => {
        setHeroImageLoaded(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [industryId]);

  if (!industry) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Industry Not Found</h1>
          <p className="text-gray-600 mb-8">The industry you're looking for doesn't exist.</p>
          <a href="/" className="bg-[#26ab4d] text-[#ffffff] px-6 py-3 rounded-lg font-semibold hover:bg-[#1e8a3f] transition-colors">
            Go Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .card-container {
          transition: all 0.5s ease-out !important;
          position: relative;
          overflow: visible;
          border-radius: 1rem;
          background: black;
          border: 1px solid rgba(38, 171, 77, 0.2);
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @media (min-width: 1024px) {
          .card-container {
            height: 280px;
          }
        }
        .card-container::-webkit-scrollbar {
          display: none;
        }
        .card-container > div:first-child {
          height: calc(100% - 40px);
          min-height: calc(100% - 40px);
          margin-top: 40px;
          display: flex;
          flex-direction: column;
          position: relative;
          z-index: 2;
          transition: transform 0.5s ease-out;
          border-radius: 0 0 1rem 1rem;
          overflow: visible;
        }
        @media (min-width: 1024px) {
          .card-container > div:first-child {
            height: 230px;
            min-height: 230px;
            margin-top: 60px;
          }
        }
        .card-container:hover > div:first-child {
          transform: translateX(calc(-100% + 10px));
        }
        @media (min-width: 1024px) {
          .card-container:hover > div:first-child {
            transform: translateY(160px);
          }
        }
        .card-container .card-title-container {
          transition: transform 0.5s ease-out;
          position: relative;
          z-index: 10;
        }
        .card-container:hover .card-title-container {
          transform: translateY(0px);
        }
        .card-container .expand-content {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 100%;
          padding: 0.75rem 1rem;
          padding-top: 0.5rem;
          padding-bottom: 1rem;
          z-index: 1;
          word-wrap: break-word;
          overflow-wrap: break-word;
          max-width: 100%;
          border-radius: 1rem;
          overflow-y: auto;
          overflow-x: hidden;
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @media (min-width: 1024px) {
          .card-container .expand-content {
            padding: 1rem 1.5rem;
            padding-bottom: 1.5rem;
          }
        }
        .card-container .expand-content::-webkit-scrollbar {
          display: none;
        }
        .card-container .expand-content > div:first-child {
          opacity: 1;
          visibility: visible;
          transition: opacity 0.3s ease-out, visibility 0.3s ease-out;
        }
        .card-container:hover .expand-content > div:first-child {
          opacity: 1;
          visibility: visible;
        }
        .card-container .expand-content > div {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .card-container .expand-content p {
          max-width: 100%;
          text-overflow: ellipsis;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
      `}</style>
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Background Gradient - Applied to entire page (matching ProductPage hero) */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#26ab4d]/25 via-black to-[#26ab4d]/25"></div>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(circle at 20% 30%, rgba(38, 171, 77, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(38, 171, 77, 0.15) 0%, transparent 50%)'
        }}></div>
      </div>
      {/* Universal Industry Hero Section with Text Left, Vector Right */}
      <section id="hero" className="h-[75vh] xl:h-[85vh] relative overflow-hidden bg-transparent">
        <div className="relative z-30 h-full flex items-center pt-[7rem] lg:pt-[10rem] xl:pt-[14rem] pb-[2rem] lg:pb-[2.5rem] xl:pb-[4rem]">
          <div className="container mx-auto px-[1.5rem] w-full">
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-[1.25rem] lg:gap-[1.75rem] xl:gap-[2.5rem] items-center">
			{/* Left Side - Text Content (shown second on mobile, centered) */}
			<div className="space-y-[0.875rem] lg:space-y-[1.25rem] xl:space-y-[1.5rem] lg:max-w-[90%] order-2 lg:order-1 text-center lg:text-left">
                  <div className="inline-block lg:inline-block">
                    <div>
                      <h1 className="text-[1.5rem] lg:text-[1.75rem] xl:text-[2.5rem] 2xl:text-[3rem] font-black text-[#ffffff] leading-tight mb-2 lg:mb-4 xl:mb-6 whitespace-nowrap">
                        {industry.name}
                      </h1>
                      <h2 className="text-[1.25rem] lg:text-[1.5rem] xl:text-[2rem] 2xl:text-[2.25rem] font-bold text-[#26ab4d] mb-2 lg:mb-4 xl:mb-6 whitespace-nowrap">
                        {industry.punchyLine}
                      </h2>
                    </div>
                    
                    <p className="block text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] text-[#ffffff] leading-relaxed tracking-wider mx-0 text-left">
                      {industry.description}
                    </p>
                  </div>
                </div>

			{/* Right Side - Industry Hero Image (shown first on mobile) */}
				<div className="flex justify-center lg:justify-end lg:items-start order-1 lg:order-2 lg:ml-8 xl:ml-12">
					<div className="relative w-[200px] h-[200px] lg:w-[380px] lg:h-[320px] xl:w-[500px] xl:h-[400px] flex items-center justify-center overflow-hidden mt-10 lg:self-start lg:mt-[1rem] xl:mt-[1.5rem]">
                  {industry.id === 'banking' ? (
                    <>
                      <img 
                        src="/industry-hero-icons/banking fraud.png" 
                        alt={`${industry.name} Hero Illustration`}
                        className="w-full h-full transition-opacity duration-1000 ease-in-out transform scale-200 object-cover"
                        style={{
                          backgroundColor: 'transparent',
                          mixBlendMode: 'normal',
                          objectFit: 'cover',
                          objectPosition: 'center center',
                          opacity: heroImageLoaded ? 0 : 1
                        }}
                        loading="eager"
                        decoding="async"
                        fetchPriority="high"
                        width={500}
                        height={500}
                        onLoad={() => {
                          if (heroImageLoaded) {
                            setTimeout(() => {
                              setHeroImageLoaded(false);
                            }, 50);
                          }
                        }}
                        onError={() => setHeroImageLoaded(false)}
                      />
                    </>
                  ) : industry.id === 'nbfc' ? (
                    <>
                      <img 
                        src="/industry-hero-icons/lending fraud.png" 
                        alt={`${industry.name} Hero Illustration`}
                        className="w-full h-full transition-opacity duration-1000 ease-in-out transform scale-200 object-cover"
                        style={{
                          backgroundColor: 'transparent',
                          mixBlendMode: 'normal',
                          objectFit: 'cover',
                          objectPosition: 'center center',
                          opacity: heroImageLoaded ? 0 : 1
                        }}
                        loading="eager"
                        decoding="async"
                        fetchPriority="high"
                        width={500}
                        height={500}
                        onLoad={() => {
                          if (heroImageLoaded) {
                            setTimeout(() => {
                              setHeroImageLoaded(false);
                            }, 50);
                          }
                        }}
                        onError={() => setHeroImageLoaded(false)}
                      />
                    </>
                  ) : industry.id === 'insurance' ? (
                    <>
                      <img 
                        src="/industry-hero-icons/insurance fraud.png" 
                        alt={`${industry.name} Hero Illustration`}
                        className="w-full h-full object-contain transition-opacity duration-1000 ease-in-out transform scale-100"
                        style={{
                          backgroundColor: 'transparent',
                          mixBlendMode: 'normal',
                          objectFit: 'contain',
                          objectPosition: 'center center',
                          opacity: heroImageLoaded ? 0 : 1,
                          alignSelf: 'center',
                          marginTop: 'auto',
                          marginBottom: 'auto'
                        }}
                        loading="eager"
                        decoding="async"
                        fetchPriority="high"
                        width={350}
                        height={350}
                        onLoad={() => {
                          if (heroImageLoaded) {
                            setTimeout(() => {
                              setHeroImageLoaded(false);
                            }, 50);
                          }
                        }}
                        onError={() => setHeroImageLoaded(false)}
                      />
                    </>
                  ) : industry.id === 'gaming' ? (
                    <>
                      <img 
                        src="/industry-hero-icons/game fraud.png" 
                        alt={`${industry.name} Hero Illustration`}
                        className="w-full h-full transition-opacity duration-1000 ease-in-out transform scale-200 object-cover"
                        style={{
                          backgroundColor: 'transparent',
                          mixBlendMode: 'normal',
                          objectFit: 'cover',
                          objectPosition: 'center center',
                          opacity: heroImageLoaded ? 0 : 1
                        }}
                        loading="eager"
                        decoding="async"
                        fetchPriority="high"
                        width={500}
                        height={500}
                        onLoad={() => {
                          if (heroImageLoaded) {
                            setTimeout(() => {
                              setHeroImageLoaded(false);
                            }, 50);
                          }
                        }}
                        onError={() => setHeroImageLoaded(false)}
                      />
                    </>
                  ) : industry.id === 'matrimony' ? (
                    <>
                      <img 
                        src="/industry-hero-icons/matrimony fraud.png" 
                        alt={`${industry.name} Hero Illustration`}
                        className="w-full h-full transition-opacity duration-1000 ease-in-out transform scale-200 object-cover"
                        style={{
                          backgroundColor: 'transparent',
                          mixBlendMode: 'normal',
                          objectFit: 'cover',
                          objectPosition: 'center center',
                          opacity: heroImageLoaded ? 0 : 1
                        }}
                        loading="eager"
                        decoding="async"
                        fetchPriority="high"
                        width={500}
                        height={500}
                        onLoad={() => {
                          if (heroImageLoaded) {
                            setTimeout(() => {
                              setHeroImageLoaded(false);
                            }, 50);
                          }
                        }}
                        onError={() => setHeroImageLoaded(false)}
                      />
                    </>
                  ) : industry.id === 'ecommerce' ? (
                    <>
                      <img 
                        src="/industry-hero-icons/ecommerce fraud.png" 
                        alt={`${industry.name} Hero Illustration`}
                        className="w-full h-full transition-opacity duration-1000 ease-in-out transform scale-200 object-cover"
                        style={{
                          backgroundColor: 'transparent',
                          mixBlendMode: 'normal',
                          objectFit: 'cover',
                          objectPosition: 'center center',
                          opacity: heroImageLoaded ? 0 : 1
                        }}
                        loading="eager"
                        decoding="async"
                        fetchPriority="high"
                        width={500}
                        height={500}
                        onLoad={() => {
                          if (heroImageLoaded) {
                            setTimeout(() => {
                              setHeroImageLoaded(false);
                            }, 50);
                          }
                        }}
                        onError={() => setHeroImageLoaded(false)}
                      />
                    </>
                  ) : (
                    <div className={`transition-opacity duration-1000 ease-in-out ${heroImageLoaded ? 'opacity-0' : 'opacity-100'}`}>
                      {getIndustryVector(industry.id)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumb Navigation - Positioned below video */}
      <div className="relative z-20 bg-transparent py-3 sm:py-4">
        <div className="container mx-auto px-[1.5rem]">
          <nav className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-[0.875rem] lg:text-[0.9375rem] xl:text-[1rem] font-bold w-fit flex-wrap">
            <Link 
              to="/" 
              className="text-[#ffffff] hover:text-[#26ab4d] transition-colors"
            >
              Home
            </Link>
            <span className="text-[#26ab4d]">/</span>
            <span className="text-[#ffffff]">Use Cases</span>
            <span className="text-[#26ab4d]">/</span>
            <span className="text-[#ffffff]">{industry.name}</span>
          </nav>
        </div>
      </div>

      {/* Content Section */}
      <div className="pt-[1rem] sm:pt-[1.5rem] pb-[2.5rem] sm:pb-[3rem] lg:pb-[5rem] bg-transparent relative z-10">
        <div className="container mx-auto px-[1.5rem] sm:px-[2rem] relative z-30">
          <div className="w-full">
            
            {/* Fraud Landscape */}
            <div className="mb-[2rem] sm:mb-[2.5rem] lg:mb-[3rem] xl:mb-[4rem] rounded-lg sm:rounded-xl lg:rounded-3xl py-3 sm:py-4 lg:py-4 px-3 sm:px-4 lg:px-8 xl:px-10" style={{
              backgroundColor: 'rgba(50, 0, 0, 0.5)'
            }}>
              <div className="text-center mb-3 sm:mb-4 xl:mb-6 pt-2 sm:pt-2.5 xl:pt-4 px-2 sm:px-0">
                <h2 className="text-[1.125rem] sm:text-[1.375rem] lg:text-[1.75rem] xl:text-[2rem] 2xl:text-[2.5rem] font-black text-[#ffffff] leading-tight break-words">
                  {industry.name} Fraud <span className="text-[#c42613]">Landscape</span>
                </h2>
              </div>
              {/* Two-column layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-[1.5rem] lg:gap-[2rem] xl:gap-[2.5rem] items-start">
                  {/* Left: Punch line, description, then three text items */}
                  <div className="flex flex-col h-full space-y-3 sm:space-y-3.5 xl:space-y-[2rem]">
                    <div className="bg-transparent rounded-lg sm:rounded-xl lg:rounded-3xl p-3.5 sm:p-4 lg:p-7 xl:p-8">
                      <h3 className="text-[1.0625rem] sm:text-[1.125rem] lg:text-[1.5rem] xl:text-[2rem] font-bold text-[#c42613] leading-tight m-0 break-words mb-2 sm:mb-2.5 lg:mb-3">
                        {fraudPunchlineText}
                      </h3>
                      <p className="text-[0.8125rem] sm:text-[0.875rem] lg:text-[0.875rem] xl:text-[1rem] text-[#ffffff] leading-relaxed tracking-wider max-w-full lg:max-w-2xl break-words">
                        {fraudDescriptionText}
                      </p>
                    </div>
                    
                  </div>

                  {/* Right: Stats top + illustrative image below (no panel) */}
                  <div className="flex flex-col gap-3 sm:gap-4 lg:gap-6 h-full">
                    <div className="bg-transparent rounded-lg sm:rounded-xl lg:rounded-3xl p-2.5 sm:p-3 lg:p-8">
                      <div className="grid grid-cols-2 gap-2.5 sm:gap-3 lg:gap-4 xl:gap-6">
                        {isLending ? (
                          <>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.375rem] sm:text-[1.5rem] lg:text-[1.875rem] xl:text-[2.25rem] font-bold text-[#c42613] mb-1.5 sm:mb-2 lg:mb-2">
                                <CountUp target={2200} suffix="+" />
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs leading-tight break-words">Fake lending apps removed in a year.</div>
                            </div>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.375rem] sm:text-[1.5rem] lg:text-[1.875rem] xl:text-[2.25rem] font-bold text-[#c42613] mb-1.5 sm:mb-2 lg:mb-2">
                                <CountUp target={761} />
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs leading-tight break-words">Cyber threats per minute targeting financial systems.</div>
                            </div>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.375rem] sm:text-[1.625rem] lg:text-3xl xl:text-4xl font-bold text-[#c42613] mb-1.5 sm:mb-2 lg:mb-2 flex items-baseline">
                                <span className="text-base sm:text-lg lg:text-2xl">₹</span>
                                <CountUp target={33000} suffix="+ " />
                                <span className="text-base sm:text-lg lg:text-2xl ml-1">C<span className="text-[0.625rem] sm:text-xs lg:text-base">r</span></span>
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs leading-tight break-words">Loan fraud reported in FY25 triple YoY.</div>
                            </div>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-2xl lg:text-3xl xl:text-4xl font-bold text-[#c42613] mb-1.5 lg:mb-2 flex items-baseline">
                                <CountUp target={35} suffix="" />
                                <span className="text-xs lg:text-base">%</span>
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs leading-tight break-words">New loans from small-ticket digital lending.</div>
                            </div>
                          </>
                        ) : isInsurance ? (
                          <>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.5rem] sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-[#c42613] mb-1.5 sm:mb-2 flex items-baseline">
                                <span className="text-base sm:text-xl lg:text-2xl">₹</span>
                                <CountUp target={30401} suffix=" " />
                                <span className="text-base sm:text-xl lg:text-2xl ml-1">C<span className="text-xs sm:text-base">r</span></span>
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs leading-tight break-words">Lost yearly ~8.5% industry revenue drained by fraud.</div>
                            </div>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.5rem] sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-[#c42613] mb-1.5 sm:mb-2 flex items-baseline">
                                <CountUp target={86} suffix="" />
                                <span className="text-xs sm:text-base">%</span>
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs break-words">Fraud concentrated in life insurance.</div>
                            </div>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.5rem] sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-[#c42613] mb-1.5 sm:mb-2 flex items-baseline">
                                <CountUp target={15} suffix="" />
                                <span className="text-xs sm:text-base">%</span>
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs break-words">Of claims flagged as fraudulent.</div>
                            </div>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.5rem] sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-[#c42613] mb-1.5 sm:mb-2 flex items-baseline">
                                <CountUp target={60} suffix="" />
                                <span className="text-xs sm:text-base">%</span>
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs break-words">Insurers report a fraud surge from onboarding gaps.</div>
                            </div>
                          </>
                        ) : isGaming ? (
                          <>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.5rem] sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-[#c42613] mb-1.5 sm:mb-2 flex items-baseline">
                                <CountUp target={64} suffix="" />
                                <span className="text-xs sm:text-base">%</span>
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs break-words">Spike in global gaming fraud.</div>
                            </div>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.5rem] sm:text-[1.875rem] lg:text-[2.25rem] font-bold text-[#c42613] mb-1.5 sm:mb-2">
                                <CountUp target={1200} prefix="$" suffix="M+" />
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs break-words">Losses from mobile betting & casino fraud.</div>
                            </div>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.5rem] sm:text-[1.875rem] lg:text-[2.25rem] font-bold text-[#c42613] mb-1.5 sm:mb-2">
                                <CountUp target={400} suffix="M+" />
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs break-words">Indian gamers exposed to identity attacks, ATOs & laundering.</div>
                            </div>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.5rem] sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-[#c42613] mb-1.5 sm:mb-2 flex items-baseline">
                                <CountUp target={66} suffix="" />
                                <span className="text-xs sm:text-base">%</span>
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs break-words">Fraud driven by bonus abuse & multi-accounting.</div>
                            </div>
                           </>
                        ) : isEcommerce ? (
                          <>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.5rem] sm:text-[1.875rem] lg:text-[2.25rem] font-bold text-[#c42613] mb-1.5 sm:mb-2">
                                <CountUp target={48} prefix="$" suffix="B+" />
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs break-words">Lost yearly to e-commerce fraud.</div>
                            </div>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.5rem] sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-[#c42613] mb-1.5 sm:mb-2 flex items-baseline">
                                <CountUp target={3} suffix="" />
                                <span className="text-xs sm:text-base">%</span>
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs break-words">Average revenue lost to fraud.</div>
                            </div>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.5rem] sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-[#c42613] mb-1.5 sm:mb-2 flex items-baseline">
                                <CountUp target={101} suffix="" />
                                <span className="text-xs sm:text-base">%</span>
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs break-words">E-commerce fraud attacks surge in India (ATO & mules).</div>
                            </div>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.5rem] sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-[#c42613] mb-1.5 sm:mb-2 flex items-baseline">
                                <CountUp target={33} suffix="" />
                                <span className="text-xs sm:text-base">%</span>
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs break-words">Shoppers using RTP hit at least once.</div>
                            </div>
                          </>
                        ) : isMatrimony ? (
                          <>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.5rem] sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-[#c42613] mb-1.5 sm:mb-2 flex items-baseline">
                                <CountUp target={78} suffix="" />
                                <span className="text-xs sm:text-base">%</span>
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs break-words">Women encountering fake or manipulated profiles.</div>
                            </div>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.5rem] sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-[#c42613] mb-1.5 sm:mb-2 flex items-baseline">
                                <CountUp target={68} suffix="" />
                                <span className="text-xs sm:text-base">%</span>
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs break-words">Victims suffering losses avg ₹12+ lakh per case.</div>
                            </div>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.5rem] sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-[#c42613] mb-1.5 sm:mb-2 flex items-baseline">
                                <span className="text-base sm:text-xl lg:text-2xl">₹</span>
                                <CountUp target={2} suffix="+ Bn" />
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs break-words">Lost to online dating & matrimony scams.</div>
                            </div>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.25rem] sm:text-[1.5rem] lg:text-[1.875rem] xl:text-[2.25rem] font-bold text-[#c42613] mb-1.5 sm:mb-2">AI/Deepfakes</div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs break-words">Surge in AI-generated identities & deepfake romance scams.</div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.5rem] sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-[#c42613] mb-1.5 sm:mb-2 flex items-baseline">
                                <span className="text-base sm:text-xl lg:text-2xl">₹</span>
                                <CountUp target={22800} suffix=" " />
                                <span className="text-base sm:text-xl lg:text-2xl ml-1">C<span className="text-xs sm:text-base">r</span></span>
                              </div>
                              <div className="text-[#ffffff] text-[0.6875rem] sm:text-[0.75rem] lg:text-xs break-words">Lost in digital fraud. 3x surge vs 2023.</div>
                            </div>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.5rem] sm:text-[1.875rem] lg:text-[2.25rem] font-bold text-[#c42613] mb-1.5 sm:mb-2">
                                <CountUp target={8} suffix="x" />
                              </div>
                              <div className="text-[#ffffff] text-[0.625rem] sm:text-xs">Spike in banking frauds.</div>
                            </div>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.5rem] sm:text-[1.875rem] lg:text-[2.25rem] font-bold text-[#c42613] mb-1.5 sm:mb-2">
                                <CountUp target={3} suffix="x" />
                              </div>
                              <div className="text-[#ffffff] text-[0.625rem] sm:text-xs">Losses up to ₹36,000 Cr despite 34% fewer cases.</div>
                            </div>
                            <div className="rounded-lg sm:rounded-xl lg:rounded-2xl p-2.5 sm:p-3 lg:p-4 transition-colors flex flex-col aspect-square lg:aspect-auto justify-center">
                              <div className="text-[1.25rem] sm:text-[1.5rem] lg:text-[1.875rem] xl:text-[2.25rem] font-bold text-[#c42613] mb-1.5 sm:mb-2">Regulatory</div>
                              <div className="text-[#ffffff] text-[0.625rem] sm:text-xs">Draft norms: risk-based monitoring, consent, accountability.</div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    
                  </div>
                </div>
            </div>
            
            {/* Industry Requirements */}
            <div className="mb-[2rem] sm:mb-[2.5rem] lg:mb-[3rem] xl:mb-[4rem]">
              <div className="text-center mb-2.5 sm:mb-4 lg:mb-6 xl:mb-8 2xl:mb-10 pt-1.5 sm:pt-2 lg:pt-3 xl:pt-4 px-2 sm:px-0">
                <h2 className="text-[1.125rem] sm:text-[1.375rem] lg:text-[1.75rem] xl:text-[2rem] 2xl:text-[2.5rem] font-black text-[#ffffff] leading-tight break-words">
                  <span className="text-[#ffffff]">Industry Requirements </span>
                  <span className="bg-gradient-to-r from-[#26ab4d] via-[#1e8a3f] to-[#26ab4d] bg-clip-text text-transparent">- {industry.name}</span>
                </h2>
              </div>
              <div className="flex flex-col lg:grid lg:grid-cols-4 gap-3 lg:gap-4 xl:gap-6">
                {industry.industryRequirements.map((requirement, index) => {
                  // Extract title and description from the requirement text
                  const parts = requirement.split(':');
                  const title = parts[0].trim();
                  const description = parts[1] ? parts[1].trim() : '';
                  
                  return (
                    <div key={index} className="bg-black/80 rounded-lg lg:rounded-xl xl:rounded-2xl p-2 sm:p-2.5 lg:p-5 xl:p-6 border-2 border-[#26ab4d] hover:border-[#1e8a3f] transition-colors duration-300 mb-3 lg:mb-0 last:mb-0 aspect-square lg:aspect-auto w-full max-w-[200px] mx-auto lg:max-w-none lg:mx-0 lg:h-full flex flex-col justify-center items-center">
                      {/* Number Badge */}
                      <div className="w-6 h-6 sm:w-7 sm:h-7 lg:w-10 lg:h-10 xl:w-12 xl:h-12 bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f] rounded-full flex items-center justify-center mb-1.5 sm:mb-2 lg:mb-3 xl:mb-4 shadow-lg flex-shrink-0">
                        <span className="text-[#ffffff] font-bold text-xs sm:text-sm lg:text-lg xl:text-[1.125rem] leading-none">{index + 1}</span>
                      </div>
                      
                      {/* Title */}
                      <h3 className="text-[#26ab4d] font-bold text-[0.875rem] sm:text-[0.9375rem] lg:text-lg xl:text-[1.25rem] mb-1.5 sm:mb-2 lg:mb-[0.75rem] leading-tight break-words text-center w-full">
                        {title}
                      </h3>
                      
                      {/* Description */}
                      <p className="text-[#ffffff] text-[0.75rem] sm:text-[0.8125rem] lg:text-sm xl:text-[1rem] leading-relaxed text-left break-words flex-1 w-full">
                        {description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* How Bargad Helps */}
            <div className="mb-[2rem] sm:mb-[2.5rem] lg:mb-[3rem] xl:mb-[4rem]">
              <div className="text-center mb-2.5 sm:mb-4 lg:mb-8 xl:mb-10 2xl:mb-12 pt-1.5 sm:pt-2 xl:pt-4 px-2 sm:px-0">
                <h2 className="text-[1.125rem] sm:text-[1.375rem] lg:text-[1.75rem] xl:text-[2rem] 2xl:text-[2.5rem] font-black text-[#ffffff] leading-tight">
                  How <span className="text-[#26ab4d]">Bargad</span> Helps
                </h2>
              </div>
              <div className="w-full">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5 sm:gap-[1.5rem] lg:gap-[2rem] xl:gap-[2.5rem] items-center">
                  {/* Left: Interactive list (moved to right on large screens) */}
                  <div className="space-y-2.5 sm:space-y-3 lg:space-y-3 xl:space-y-4 order-2 lg:order-2 lg:ml-4 xl:ml-8 px-2 sm:px-0">
                    {helpsItems.map((item, idx) => (
                      <button
                        key={item.id}
                        onClick={() => setActiveHelpIndex(idx)}
                        className={`text-left w-full p-1.5 sm:p-2 lg:p-2 transition-colors`}
                      >
                        <h3 className={`text-[#26ab4d] hover:text-[#1e8a3f] font-semibold text-[1.125rem] sm:text-[1.25rem] lg:text-lg xl:text-[1.25rem] mb-1.5 sm:mb-2 lg:mb-2 transition-colors break-words leading-tight`}>
                          {item.title}
                        </h3>
                        <p className="text-[#ffffff] text-[0.875rem] sm:text-[0.9375rem] lg:text-sm xl:text-[1rem] leading-relaxed max-w-full lg:max-w-md">
                          {item.description}
                        </p>
                      </button>
                    ))}
                  </div>

                  {/* Right: Industry-specific "How Bargad Helps" vector (on left on large screens) */}
                  <div className="rounded-xl sm:rounded-2xl overflow-hidden bg-transparent min-h-[120px] h-[120px] sm:h-[180px] md:h-[250px] lg:h-[400px] xl:h-[500px] flex items-center justify-center order-1 lg:order-1 px-2 sm:px-0">
                    <img
                      src={howBargadHelpsImage}
                      alt={`How Bargad Helps ${industry.name}`}
                      className="w-full h-full object-contain"
                      loading="lazy"
                      decoding="async"
                      width="600"
                      height="400"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/design1.png'; }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Key capabilities rtext */}
            <div className="mb-[2rem] sm:mb-[2.5rem] lg:mb-[3rem] xl:mb-[4rem]">
              <div className="text-center mb-2.5 sm:mb-4 lg:mb-6 xl:mb-8 2xl:mb-10 pt-1.5 sm:pt-2 lg:pt-3 xl:pt-4 px-2 sm:px-0">
                <h2 className="text-[1.125rem] sm:text-[1.375rem] lg:text-[1.75rem] xl:text-[2rem] 2xl:text-[2.5rem] font-black text-[#ffffff] leading-tight">
                  Key <span className="text-[#26ab4d]">Capabilities</span>
                </h2>
              </div>
              
              <div className="w-full">
                <div className="flex flex-col lg:grid lg:grid-cols-2 xl:grid-cols-4 gap-3 lg:gap-4 xl:gap-6">
                  
                  {/* Card 1: Rounded Rectangle (business card) */}
                  <div className="card-container group w-full max-w-[200px] mx-auto lg:max-w-none lg:mx-0 mb-4 lg:mb-0 last:mb-0 aspect-square lg:aspect-auto">
                    <div className="bg-gradient-to-br from-[#26ab4d] to-[#1e8a3f] shadow-lg overflow-hidden hover:shadow-2xl border border-transparent transition-all duration-300 rounded-lg lg:rounded-xl xl:rounded-2xl relative">
                      <div className="p-2.5 sm:p-3 lg:p-5 xl:p-6 2xl:p-8 relative flex items-center justify-center h-full card-title-container min-h-[55px] sm:min-h-[65px] lg:min-h-[100px]">
                        <h3 className="text-[0.7rem] sm:text-[0.9375rem] lg:text-lg xl:text-xl font-black text-[#ffffff] text-center leading-tight px-1 break-words">{keyCapTitles[0]}</h3>
                      </div>
                    </div>
                    <div className="expand-content bg-black relative">
                      {/* Animation on black container - visible on hover */}
                      <div className="absolute top-1.5 sm:top-2 lg:top-3 left-1.5 sm:left-2 lg:left-4 z-30">
                        <div className="relative w-4 h-4 sm:w-5 sm:h-5 lg:w-7 lg:h-7 xl:w-8 xl:h-8">
                          <div className="absolute top-0.5 sm:top-1 left-0.5 sm:left-1 w-1 h-1 sm:w-1.5 sm:h-1.5 lg:w-2 lg:h-2 bg-[#26ab4d] rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                          <div className="absolute top-0.5 sm:top-1 right-0.5 sm:right-1 w-1 h-1 sm:w-1.5 sm:h-1.5 lg:w-2 lg:h-2 bg-[#26ab4d] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          <div className="absolute bottom-0.5 sm:bottom-1 right-0.5 sm:right-1 w-1 h-1 sm:w-1.5 sm:h-1.5 lg:w-2 lg:h-2 bg-[#26ab4d] rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                          <div className="absolute bottom-0.5 sm:bottom-1 left-0.5 sm:left-1 w-1 h-1 sm:w-1.5 sm:h-1.5 lg:w-2 lg:h-2 bg-[#26ab4d] rounded-full animate-bounce" style={{ animationDelay: '0.6s' }}></div>
                        </div>
                      </div>
                      <div className="flex flex-col h-full items-start justify-center">
                        <ul className="space-y-1.5 sm:space-y-2 lg:space-y-3 flex-1 pt-8 sm:pt-10 lg:pt-10 xl:pt-12 2xl:pt-14 px-2 sm:px-2.5 lg:px-3 pb-3 sm:pb-4 w-full">
                          {keyCapBenefits[0].map((b, i) => (
                            <li key={i} className="text-[#ffffff] text-[0.65rem] sm:text-[0.8125rem] lg:text-sm text-left pl-2 sm:pl-2.5 lg:pl-2.5 border-l-2 border-[#26ab4d]/50 leading-relaxed">{b}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Rounded Rectangle */}
                  <div className="card-container group w-full max-w-[200px] mx-auto lg:max-w-none lg:mx-0 mb-4 lg:mb-0 last:mb-0 aspect-square lg:aspect-auto">
                    <div className="bg-gradient-to-br from-[#26ab4d] to-[#1e8a3f] shadow-lg overflow-hidden hover:shadow-2xl border border-transparent transition-all duration-300 rounded-lg lg:rounded-xl xl:rounded-2xl relative">
                      <div className="p-2.5 sm:p-3 lg:p-5 xl:p-6 2xl:p-8 relative flex items-center justify-center h-full card-title-container min-h-[55px] sm:min-h-[65px] lg:min-h-[100px]">
                        <h3 className="text-[0.7rem] sm:text-[0.9375rem] lg:text-lg xl:text-xl font-black text-[#ffffff] text-center leading-tight px-1 break-words">{keyCapTitles[1]}</h3>
                      </div>
                    </div>
                    <div className="expand-content bg-black relative">
                      {/* Animation on black container - visible on hover */}
                      <div className="absolute top-1.5 sm:top-2 lg:top-3 left-1.5 sm:left-2 lg:left-4 z-30">
                        <div className="relative w-4 h-4 sm:w-5 sm:h-5 lg:w-7 lg:h-7 xl:w-8 xl:h-8">
                          <div className="absolute inset-0 border-2 border-[#26ab4d] border-t-transparent rounded-full animate-spin" style={{ animationDuration: '1.2s' }}></div>
                          <div className="absolute inset-0.5 sm:inset-1 lg:inset-2 border-2 border-[#26ab4d] border-b-transparent rounded-full animate-spin" style={{ animationDuration: '0.9s', animationDirection: 'reverse' }}></div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 lg:w-2 lg:h-2 bg-[#26ab4d] rounded-full animate-pulse"></div>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col h-full items-start justify-center">
                        <ul className="space-y-1.5 sm:space-y-2 lg:space-y-3 flex-1 pt-8 sm:pt-10 lg:pt-10 xl:pt-12 2xl:pt-14 px-2 sm:px-2.5 lg:px-3 pb-3 sm:pb-4 w-full">
                          {keyCapBenefits[1].map((b, i) => (
                            <li key={i} className="text-[#ffffff] text-[0.65rem] sm:text-[0.8125rem] lg:text-sm text-left pl-2 sm:pl-2.5 lg:pl-2.5 border-l-2 border-[#26ab4d]/50 leading-relaxed">{b}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Card 3: Rounded Rectangle */}
                  <div className="card-container group w-full max-w-[200px] mx-auto lg:max-w-none lg:mx-0 mb-4 lg:mb-0 last:mb-0 aspect-square lg:aspect-auto">
                    <div className="bg-gradient-to-br from-[#26ab4d] to-[#1e8a3f] shadow-lg overflow-hidden hover:shadow-2xl border border-transparent transition-all duration-300 rounded-lg lg:rounded-xl xl:rounded-2xl relative">
                      <div className="p-2.5 sm:p-3 lg:p-5 xl:p-6 2xl:p-8 relative flex items-center justify-center h-full card-title-container min-h-[55px] sm:min-h-[65px] lg:min-h-[100px]">
                        <h3 className="text-[0.7rem] sm:text-[0.9375rem] lg:text-lg xl:text-xl font-black text-[#ffffff] text-center leading-tight px-1 break-words">{keyCapTitles[2]}</h3>
                      </div>
                    </div>
                    <div className="expand-content bg-black relative">
                      {/* Animation on black container - visible on hover */}
                      <div className="absolute top-1.5 sm:top-2 lg:top-3 left-1.5 sm:left-2 lg:left-4 z-30">
                        <div className="relative w-4 h-4 sm:w-5 sm:h-5 lg:w-7 lg:h-7 xl:w-8 xl:h-8 flex items-center justify-center">
                          <div className="flex flex-col gap-0.5 sm:gap-0.75 lg:gap-1">
                            <div className="w-4 h-0.5 sm:w-5 sm:h-0.5 lg:w-7 lg:h-1 xl:w-8 xl:h-1 bg-[#26ab4d] rounded-full animate-pulse" style={{ animationDelay: '0s', transform: 'scaleX(1)' }}></div>
                            <div className="w-4 h-0.5 sm:w-5 sm:h-0.5 lg:w-7 lg:h-1 xl:w-8 xl:h-1 bg-[#26ab4d] rounded-full animate-pulse" style={{ animationDelay: '0.2s', transform: 'scaleX(0.7)' }}></div>
                            <div className="w-4 h-0.5 sm:w-5 sm:h-0.5 lg:w-7 lg:h-1 xl:w-8 xl:h-1 bg-[#26ab4d] rounded-full animate-pulse" style={{ animationDelay: '0.4s', transform: 'scaleX(0.5)' }}></div>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col h-full items-start justify-center">
                        <ul className="space-y-1.5 sm:space-y-2 lg:space-y-3 flex-1 pt-8 sm:pt-10 lg:pt-10 xl:pt-12 2xl:pt-14 px-2 sm:px-2.5 lg:px-3 pb-3 sm:pb-4 w-full">
                          {keyCapBenefits[2].map((b, i) => (
                            <li key={i} className="text-[#ffffff] text-[0.65rem] sm:text-[0.8125rem] lg:text-sm text-left pl-2 sm:pl-2.5 lg:pl-2.5 border-l-2 border-[#26ab4d]/50 leading-relaxed">{b}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Card 4: Rounded Rectangle */}
                  <div className="card-container group w-full max-w-[200px] mx-auto lg:max-w-none lg:mx-0 mb-4 lg:mb-0 last:mb-0 aspect-square lg:aspect-auto">
                    <div className="bg-gradient-to-br from-[#26ab4d] to-[#1e8a3f] shadow-lg overflow-hidden hover:shadow-2xl border border-transparent transition-all duration-300 rounded-lg lg:rounded-xl xl:rounded-2xl relative">
                      <div className="p-2.5 sm:p-3 lg:p-5 xl:p-6 2xl:p-8 relative flex items-center justify-center h-full card-title-container min-h-[55px] sm:min-h-[65px] lg:min-h-[100px]">
                        <h3 className="text-[0.7rem] sm:text-[0.9375rem] lg:text-lg xl:text-xl font-black text-[#ffffff] text-center leading-tight px-1 break-words">{keyCapTitles[3]}</h3>
                      </div>
                    </div>
                    <div className="expand-content bg-black relative">
                      {/* Animation on black container - visible on hover */}
                      <div className="absolute top-1.5 sm:top-2 lg:top-3 left-1.5 sm:left-2 lg:left-4 z-30">
                        <div className="relative w-4 h-4 sm:w-5 sm:h-5 lg:w-7 lg:h-7 xl:w-8 xl:h-8">
                          {/* Shield shape */}
                          <svg className="absolute inset-0 w-full h-full text-[#26ab4d]" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2L4 5v6c0 5.55 3.84 10.74 8 12 4.16-1.26 8-6.45 8-12V5l-8-3z" className="animate-pulse" style={{ animationDuration: '2s' }} />
                          </svg>
                          {/* Checkmark inside shield */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <svg className="w-2 h-2 sm:w-2.5 sm:h-2.5 lg:w-3 lg:h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" className="animate-pulse" style={{ animationDelay: '0.3s', animationDuration: '1.5s' }} />
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col h-full items-start justify-center">
                        <ul className="space-y-1.5 sm:space-y-2 lg:space-y-3 flex-1 pt-8 sm:pt-10 lg:pt-10 xl:pt-12 2xl:pt-14 px-2 sm:px-2.5 lg:px-3 pb-3 sm:pb-4 w-full">
                          {keyCapBenefits[3].map((b, i) => (
                            <li key={i} className="text-[#ffffff] text-[0.65rem] sm:text-[0.8125rem] lg:text-sm text-left pl-2 sm:pl-2.5 lg:pl-2.5 border-l-2 border-[#26ab4d]/50 leading-relaxed">{b}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* CTA Section - Rounded & Bargad Green */}
            <div className="text-center pt-[2rem] sm:pt-[4rem] lg:pt-[8rem] pb-[2rem] sm:pb-[3rem] lg:pb-[5rem] px-2 sm:px-0">
              <div className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/10 backdrop-blur-sm border border-[#26ab4d]/30 rounded-xl sm:rounded-2xl lg:rounded-3xl p-3 sm:p-5 lg:p-8 max-w-fit mx-auto">
                <h3 className="text-[1.125rem] sm:text-[1.375rem] lg:text-[2rem] xl:text-[2.25rem] font-bold text-[#ffffff] mb-2 sm:mb-3 leading-tight break-words px-1 sm:px-0">
                  Ready to Transform Your <span className="text-[#26ab4d]">{industry.name}</span> Security?
                </h3>
                
                <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 lg:gap-4 justify-center items-center">
                  <Link 
                    to="/ask-access" 
                    id="industry-cta-button" 
                    className="bg-[#26ab4d] hover:bg-[#1e8a3f] text-[#ffffff] px-4 sm:px-6 lg:px-8 py-2 sm:py-2.5 lg:py-4 rounded-full text-xs sm:text-sm lg:text-lg font-bold transition-all duration-300 shadow-xl inline-flex items-center justify-center w-fit sm:w-auto sm:min-w-[140px] lg:min-w-[170px] whitespace-nowrap"
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                      Ask for access
              </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Contact Popup Modal */}
    {showContactPopup && (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowContactPopup(false)}>
        <div className="bg-black border-2 border-[#26ab4d] rounded-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-[#ffffff]">Contact Us</h3>
            <button
              onClick={() => setShowContactPopup(false)}
              className="text-[#ffffff] hover:text-[#ffffff] transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="space-y-4">
            {/* Phone Number */}
            <div className="flex items-center space-x-3 p-4 bg-[#26ab4d]/10 border border-[#26ab4d]/30 rounded-lg">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-[#26ab4d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-[#ffffff]">Phone Number</p>
                <a href="tel:+919920042243" className="text-[#ffffff] text-lg font-semibold hover:text-[#26ab4d] transition-colors">
                  +919920042243
                </a>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-center space-x-3 p-4 bg-[#26ab4d]/10 border border-[#26ab4d]/30 rounded-lg">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-[#26ab4d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-[#ffffff]">Email Address</p>
                <a href="mailto:info@bargad.ai" className="text-[#ffffff] text-lg font-semibold hover:text-[#26ab4d] transition-colors">
                  info@bargad.ai
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default IndustryPage;
